import React from 'react';
import { Table, Tag, Button, Space, Typography } from 'antd';
import { EyeOutlined, UserOutlined, TeamOutlined } from '@ant-design/icons';
import type { CustomerDTO, PageCustomerDTO } from '@/shared/api/model';
import type { ColumnsType } from 'antd/es/table';

const { Text } = Typography;

interface CustomerSearchResultsProps {
  data?: PageCustomerDTO | null;
  loading?: boolean;
  onViewCustomer: (branch: string, custNo: string) => void;
  onPageChange: (page: number, pageSize: number) => void;
}

const CustomerSearchResults: React.FC<CustomerSearchResultsProps> = ({
  data,
  loading,
  onViewCustomer,
  onPageChange,
}) => {
  const getCustomerTypeTag = (custType?: string) => {
    switch (custType) {
      case 'IND':
        return <Tag color="blue" icon={<UserOutlined />}>Individual</Tag>;
      case 'GRP':
        return <Tag color="green" icon={<TeamOutlined />}>Group</Tag>;
      case 'MEM':
        return <Tag color="orange" icon={<UserOutlined />}>Member</Tag>;
      default:
        return <Tag>{custType}</Tag>;
    }
  };

  const columns: ColumnsType<CustomerDTO> = [
    {
      title: 'Customer No',
      dataIndex: 'custNo',
      key: 'custNo',
      width: 120,
      render: (custNo, record) => (
        <Text strong>{`${record.branch}-${custNo}`}</Text>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Type',
      dataIndex: 'custType',
      key: 'custType',
      width: 120,
      render: (custType) => getCustomerTypeTag(custType),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 130,
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',
      width: 130,
    },
    {
      title: 'ID Card',
      dataIndex: 'idCard',
      key: 'idCard',
      width: 150,
    },
    {
      title: 'Category',
      dataIndex: 'categoryId',
      key: 'categoryId',
      width: 100,
      render: (categoryId) => categoryId || '-',
    },
    {
      title: 'Action',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => onViewCustomer(record.branch!, record.custNo!)}
          >
            View
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={data?.content || []}
      loading={loading}
      rowKey={(record) => `${record.branch}-${record.custNo}`}
      pagination={{
        current: data ? (data.number ?? 0) + 1 : 1,
        pageSize: data?.size || 20,
        total: data?.totalElements || 0,
        showSizeChanger: true,
        showTotal: (total) => `Total ${total} customers`,
        onChange: (page, pageSize) => onPageChange(page - 1, pageSize),
      }}
      scroll={{ x: 1000 }}
    />
  );
};

export default CustomerSearchResults;
